import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Brain, CheckCircle, XCircle } from '@phosphor-icons/react';

import { API_URL } from '../config';

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid verification link.');
      return;
    }
    const verify = async () => {
      try {
        const res = await axios.post(`${API_URL}/api/auth/verify-email`, { token });
        setStatus('success');
        setMessage(res.data.message);
      } catch (err) {
        setStatus('error');
        setMessage(err.response?.data?.detail || 'Verification failed. The link may have expired.');
      }
    };
    verify();
  }, [token]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <Brain size={40} weight="duotone" className="text-[#007AFF]" />
            <span className="font-heading font-black text-2xl tracking-tight text-white uppercase">
              Trader Reset AI
            </span>
          </Link>
        </div>

        <div className="bg-[#131313] border border-white/10 p-8 text-center" data-testid="verify-email-card">
          {status === 'verifying' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#007AFF]/10 flex items-center justify-center animate-pulse">
                <Brain size={32} className="text-[#007AFF]" />
              </div>
              <h1 className="font-heading font-bold text-2xl uppercase text-white mb-3">
                Verifying...
              </h1>
              <p className="text-[#8892B0]">Please wait while we verify your email.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#34C759]/10 flex items-center justify-center">
                <CheckCircle size={32} className="text-[#34C759]" />
              </div>
              <h1 className="font-heading font-bold text-2xl uppercase text-white mb-3">
                Email Verified
              </h1>
              <p className="text-[#8892B0] mb-6">{message}</p>
              <Link
                to="/community"
                className="inline-block px-8 py-3 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors whitespace-nowrap"
                data-testid="go-to-community"
              >
                Go to Community
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#FF3B30]/10 flex items-center justify-center">
                <XCircle size={32} className="text-[#FF3B30]" />
              </div>
              <h1 className="font-heading font-bold text-2xl uppercase text-white mb-3">
                Verification Failed
              </h1>
              <p className="text-[#8892B0] mb-6">{message}</p>
              <Link
                to="/login"
                className="inline-block px-8 py-3 bg-[#007AFF] text-white font-bold uppercase tracking-widest hover:bg-[#005bb5] transition-colors whitespace-nowrap"
                data-testid="back-to-login"
              >
                Sign In
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
